import { engineComponent, StringUtils, Logger } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { ImageElement } from "../Components/Elements/Graphics/ImageElement";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { Tweens } from "../Components/Effects/Tweens";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { ParticleGroupElement } from "../Components/Elements/Graphics/ParticleGroupElement";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";

@engineComponent(ComponentTypes.DisplayObject)
/**
 *
 */
export class BackgroundStripElement extends ContainerElement {
    private speed: number = 100;
    private duration: number = 100;
    private delay: number = 0;
    private startIndex: number;
    private stopIndex: number;
    private lastStoppedIndex: number;

    private scenes: Object = {};
    private scenesIds: Array<number> = [];
    private scenesOriginalPositions: Array<number> = [];

    private delayTimer: FrameTimerEventEmitter;

    public lastAnimationBgScene: number = 0;

    constructor() {
        super();

        this.delayTimer = CreateFrameTimer("delayTimer");
        this.delayTimer.on(FrameTimerEvents.Complete, this._play.bind(this));
    }

    public createByNode(node: Element): void {
        super.createByNode(node);

        if (this.style != null) {
            this.getNodesList(this.style.node.firstElementChild)
                .reverse()
                //.filter((node: Element) => node.hasAttribute('DataSceneId'))
                .forEach((node: Element) => {
                    if (node.hasAttribute("DataSceneId")) {
                        this.scenes[node.getAttribute("DataSceneId")] = node.nodeName;
                    }
                    this.scenesIds[node.nodeName] = node.getAttribute("DataSceneId");
                    this.scenesOriginalPositions.push(parseInt(node.getAttribute("X")));
                });
        }

        if (node.hasAttribute(BackgroundStripElementAttributes.SPEED)) {
            this.speed = parseInt(node.getAttribute(BackgroundStripElementAttributes.SPEED));
        }

        if (node.hasAttribute(BackgroundStripElementAttributes.DURATION)) {
            this.duration = parseInt(node.getAttribute(BackgroundStripElementAttributes.DURATION));
        }

        if (node.hasAttribute(BackgroundStripElementAttributes.DELAY)) {
            this.delay = parseInt(node.getAttribute(BackgroundStripElementAttributes.DELAY));
        }

        if (node.hasAttribute(BackgroundStripElementAttributes.FROM_SCENE)) {
            this.startIndex = this.getChildIndex(
                this.childs[node.getAttribute(BackgroundStripElementAttributes.FROM_SCENE)],
            );
        }

        if (node.hasAttribute(BackgroundStripElementAttributes.TO_SCENE)) {
            this.stopIndex = this.getChildIndex(
                this.childs[node.getAttribute(BackgroundStripElementAttributes.TO_SCENE)],
            );

            for (let key in this.scenes) {
                if (
                    this.scenes.hasOwnProperty(key) &&
                    this.scenes[key] === node.getAttribute(BackgroundStripElementAttributes.TO_SCENE)
                ) {
                    if (this.startIndex == null) {
                        this.setActiveScene(parseInt(key));
                    }
                }
            }
        }

        if (
            node.hasAttribute(BackgroundStripElementAttributes.PLAY) &&
            StringUtils.parseBoolean(node.getAttribute(BackgroundStripElementAttributes.PLAY))
        ) {
            if (this.delay) {
                this.delayTimer.start(this.delay);
            } else {
                this._play();
            }
        }
    }

    /**
     *
     * @param {Element} node
     * @param {Array<Element>} nodeList
     * @returns {Array<Element>}
     */
    private getNodesList(node: Element, nodeList: Array<Element> = []): Array<Element> {
        if (!node) {
            return nodeList;
        }

        if (node.nextElementSibling) {
            nodeList = this.getNodesList(node.nextElementSibling, nodeList);
        }

        return nodeList.concat(node);
    }

    /**
     *
     */
    public play() {
        this.delayTimer.stop();
        if (this.delay) {
            this.delayTimer.start(this.delay);
            this.delay = 0; //reset delay after call
        } else {
            this._play();
        }
    }

    /**
     *
     * @private
     */
    private _play() {
        for (let i = 0; i < this.children.length; i++) {
            this.children[i].visible = false;
        }

        if (this.stopIndex > this.startIndex) {
            for (let i = this.startIndex; i <= this.stopIndex; i++) {
                let element = <ImageElement | ContainerElement>this.children[i];
                element.visible = true;
            }
            this.tweenBackgroundToLeftOnce();
        } else if (this.stopIndex < this.startIndex) {
            for (let i = this.startIndex; i >= this.stopIndex; i--) {
                let element = <ImageElement | ContainerElement>this.children[i];
                element.visible = true;
            }
            this.tweenBackgroundToRightOnce();
        } else {
            let element = <ImageElement | ContainerElement>this.children[this.startIndex];
            element.visible = true;
            this.position.x = -this.scenesOriginalPositions[this.startIndex];
        }
    }

    /**
     *
     */
    private tweenBackgroundToLeftOnce(): void {
        Tweens.removeTweens(this, "position");
        Tweens.positionTween(this, {
            tweenX: [
                this.position.x,
                this.position.x +
                    (this.scenesOriginalPositions[this.startIndex] - this.scenesOriginalPositions[this.stopIndex]),
            ],
            tweenY: [this.position.y, this.position.y],
            tweenDuration: this.duration,
        });

        this.lastStoppedIndex = this.stopIndex;
    }

    /**
     *
     */
    private tweenBackgroundToRightOnce(): void {
        Tweens.removeTweens(this, "position");
        Tweens.positionTween(this, {
            tweenX: [
                this.position.x,
                this.position.x +
                    (this.scenesOriginalPositions[this.startIndex] - this.scenesOriginalPositions[this.stopIndex]),
            ],
            tweenY: [this.position.y, this.position.y],
            tweenDuration: this.duration,
        });

        this.lastStoppedIndex = this.stopIndex;
    }

    /**
     *
     * @param from  Scene identificator set in DataId.
     * @param to    Scene identificator set in DataId.
     * @param {number} duration
     * @param delay
     */
    public goToScene(from: number, to: number, duration?: number, delay?: number): BackgroundStripElement {
        if (from != null) {
            this.startIndex = this.getChildIndex(this.childs[this.scenes[from]]);
        } else {
            this.startIndex = this.lastStoppedIndex;
        }

        if (to != null) {
            this.stopIndex = this.getChildIndex(this.childs[this.scenes[to]]);
        }

        if (duration != null) {
            this.duration = duration;
        }

        if (delay != null) {
            this.delay = delay;
        }

        if (from != null) {
            if (DEBUG) {
                this.log(
                    "BackgroundStripElement: new " +
                        this.startIndex +
                        this.scenes[from] +
                        " to " +
                        this.scenes[to] +
                        " during " +
                        duration +
                        " with delay " +
                        delay,
                );
            }
        } else {
            if (DEBUG) {
                this.log(
                    "BackgroundStripElement: last " +
                        this.startIndex +
                        this.children[this.startIndex].name +
                        " to " +
                        this.scenes[to] +
                        " during " +
                        duration +
                        " with delay " +
                        delay,
                );
            }
        }

        return this;
    }

    /**
     *
     * @param {number} to   Scene identificator set in DataId.
     * @returns {BackgroundStripElement}
     */
    public setActiveScene(to: number): BackgroundStripElement {
        this.startIndex = this.stopIndex = this.getChildIndex(this.childs[this.scenes[to]]);
        this.lastStoppedIndex = this.stopIndex;

        ParticleGroupElement.DynamicKey = this.scenes[to];

        if (DEBUG) {
            this.log("BackgroundStripElement: Scene set to (" + this.startIndex + ") " + this.scenes[to]);
        }

        return this;
    }

    /**
     *
     * @returns {number}
     */
    public getLastScene(): number {
        if (this.lastStoppedIndex != null && this.children[this.lastStoppedIndex].name != null) {
            return this.scenesIds[this.children[this.lastStoppedIndex].name];
        }

        return null;
    }

    public setBackgroundAnimations(sceneIndex: number, enabled: boolean): void {
        let element = <ContainerElement>this.childs[this.scenes[sceneIndex]];
        if (element != null) {
            if (element.childs != null) {
                let animationGroup = <SwitchElement>element.childs["Animations"];
                if (animationGroup != null) {
                    if (enabled) {
                        if (animationGroup.getCurrentCase() != "PLAY") {
                            animationGroup.goToCase("PLAY");
                        }
                    } else {
                        animationGroup.goToCase("STOP");
                    }
                }
            }
        }
    }

    public setBackgroundAnimationsInInterval(
        startSceneIndex: number,
        endSceneIndex: number,
        enabled: boolean,
        includeLast: boolean = true,
    ): void {
        let firstIndex = this.getChildIndex(this.childs[this.scenes[startSceneIndex]]);
        let secondIndex = this.getChildIndex(this.childs[this.scenes[endSceneIndex]]);
        let name: string;

        if (firstIndex <= secondIndex) {
            for (let i: number = firstIndex; i <= secondIndex; i++) {
                if (i == secondIndex && !includeLast) {
                    break;
                }
                name = this.children[i].name;
                if (this.scenesIds[name] != null) {
                    this.setBackgroundAnimations(this.scenesIds[name], enabled);
                }
            }
        } else {
            for (let i: number = firstIndex; i >= secondIndex; i--) {
                name = this.children[i].name;
                if (i == secondIndex && !includeLast) {
                    break;
                }
                if (this.scenesIds[name] != null) {
                    this.setBackgroundAnimations(this.scenesIds[name], enabled);
                }
            }
        }
    }

    public setAllBackgroundAnimations(enabled: boolean): void {
        for (let key in this.scenes) {
            let sceneId = parseInt(key);
            this.setBackgroundAnimations(sceneId, enabled);
        }
    }

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @return void
     */
    protected log(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.debugGroup(compName, s);
        }
    }
}

const BackgroundStripElementAttributes = {
    /**
     *
     */
    DELAY: "Delay",
    /**
     *
     */
    DURATION: "Duration",
    /**
     *
     */
    FROM_SCENE: "FromScene",
    /**
     *
     */
    PLAY: "Play",
    /**
     *
     */
    SPEED: "Speed",
    /**
     *
     */
    TO_SCENE: "ToScene",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/BackgroundStripElement.ts
 **/